import { markInformationAsReadApi } from "@/api/services/main/information";
import { TGetInformationListDto } from "@/api/services/main/information/dtos/get-information-list.dto";
import { Linkable } from "@/components/elements/Linkable";
import { NotificationCardModal } from "@/features/information/components/NotificationCardModal";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { getDynamicRoute, isSafeRedirectUrl } from "@/utils/url";
import { Box, Divider, Typography } from "@mui/material";
import { FC, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";

export type TNotificationListModalProps = {
  data: TGetInformationListDto[];
  modal?: UseVisibleReturn | undefined;
};

export const NotificationListModal: FC<TNotificationListModalProps> = ({ data, modal }) => {
  const queryClient = useQueryClient();

  const handleNotificationClick = useCallback(
    async (notification: TGetInformationListDto) => {
      if (notification.informationReads.length === 0) {
        try {
          await markInformationAsReadApi(notification.id);
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.CHECK_UNREAD] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.FETCH_LIST] });
        } catch {}
      }
      modal?.close();
    },
    [modal, queryClient],
  );

  if (data.length === 0) {
    return (
      <Box py={5} textAlign="center">
        <Typography color={text.tertiary} fontWeight={700}>
          データがありません。
        </Typography>
      </Box>
    );
  }

  return data.map((notification, index) => {
    const defaultHref = getDynamicRoute(AppRoutes.informationDetail, { id: notification.id });
    const href = notification.redirectUrl && isSafeRedirectUrl(notification.redirectUrl) ? notification.redirectUrl : defaultHref;

    return (
      <Linkable key={notification.id} href={href} onClick={() => handleNotificationClick(notification)}>
        {index !== 0 && <Divider />}
        <NotificationCardModal data={notification} />
      </Linkable>
    );
  });
};
